import { useState } from 'react'
import { toast } from 'sonner'
import { checkpointsService } from '@/api/services/checkpoints.service'
import { PURGE_COUNTDOWN_SECONDS, PURGE_TOAST_ID } from '../constants/purge-checkpoint'

/**
 * The "Purge Checkpoint Data" flow: confirm dialog state, the purge call, and the countdown toast
 * that refreshes the page once it reaches zero.
 */
export function usePurgeCheckpoint(spreadId: string | undefined) {
  const [isPurgeDialogOpen, setIsPurgeDialogOpen] = useState(false)
  const [isPurging, setIsPurging] = useState(false)

  const handleContinuePurge = async () => {
    if (!spreadId) {
      toast.error('No spread ID available to purge')
      setIsPurgeDialogOpen(false)
      return
    }

    setIsPurging(true)
    try {
      await checkpointsService.purgeCheckpoint(spreadId)
      setIsPurgeDialogOpen(false)

      let secondsRemaining = PURGE_COUNTDOWN_SECONDS

      // Same toast id on every call re-renders the existing toast in place instead of stacking
      // new ones. `duration: Infinity` keeps it visible for the full countdown.
      toast.success(`Checkpoint data purged — refreshing in ${secondsRemaining}s…`, {
        id: PURGE_TOAST_ID,
        duration: Infinity,
      })

      const countdown = setInterval(() => {
        secondsRemaining -= 1

        if (secondsRemaining <= 0) {
          clearInterval(countdown)
          window.location.reload()
          return
        }

        toast.success(`Checkpoint data purged — refreshing in ${secondsRemaining}s…`, {
          id: PURGE_TOAST_ID,
          duration: Infinity,
        })
      }, 1000)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'

      toast.error(`Failed to purge checkpoint data: ${message}`)
      setIsPurging(false)
      setIsPurgeDialogOpen(false)
    }
  }

  return { isPurgeDialogOpen, setIsPurgeDialogOpen, isPurging, handleContinuePurge }
}
